import { useEffect, useState } from 'react';
import {
  Money,
  isFeatureOn,
  isTerminal,
  type DictItemDto,
  type OrderStatus,
} from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';

interface Order {
  id: string;
  status: OrderStatus;
  codAmount: string;
  codPending?: boolean;
  currency: string;
  specialItems?: Array<{ code: string; label: string; icon: string | null }>;
  pickedUpAt?: string | null;
}

/**
 * 改这一单的「代收多少 / 里面是什么」。
 *
 * ★ 这两样是下单之后**最常变的**:客户临时少订一件、
 *   店家装货时才发现里面有冻品。而在这之前唯一的办法是取消重下 ——
 *   单号变了、司机白跑一趟、已经派好的活要重派一遍。
 *
 * ★ 结束的单不给改:代收金额是结算的输入,而结算已经按旧数算过了。
 *   改一张送完的单等于改账。
 */
export function HandlingEditor({
  order,
  canEdit,
  features,
  onSaved,
}: {
  order: Order;
  canEdit: boolean;
  features?: Record<string, boolean>;
  onSaved: () => Promise<void> | void;
}) {
  const specialItemsOn = isFeatureOn(features, 'specialItems');
  const done = isTerminal(order.status);

  const [open, setOpen] = useState(false);
  const [cod, setCod] = useState(Number(order.codAmount) > 0 || order.codPending === true);
  /** 「要收,但还没算出来」—— 填上金额就自动不再待定 */
  const [pending, setPending] = useState(order.codPending === true);
  /*
   * ★ 输入的是**元**,不是分。
   *   让人把 120 块钱敲成 12000,是在要求他每次心算一次 ——
   *   而心算错的那次,司机会去收 1200 块。
   *   存进库里的仍然是最小单位(结算、守卫、账单都按它算),
   *   换算在**边界上做一次**,用 Money.tryFromMajor(不碰浮点,
   *   而且对「120.」这种敲了一半的输入不抛 —— 抛的话整棵树会被卸载)。
   */
  const [amount, setAmount] = useState<string>(
    Number(order.codAmount) > 0 ? Money.toMajorString(Money.minor(Number(order.codAmount))) : '',
  );
  const [items, setItems] = useState<string[]>((order.specialItems ?? []).map((i) => i.code));
  const [dict, setDict] = useState<DictItemDto[]>([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !specialItemsOn) return;
    void api<DictItemDto[]>('/dict/SPECIAL_ITEM')
      .then(setDict)
      .catch(() => undefined);
  }, [open, specialItemsOn]);

  /*
   * ★ 每次打开都从**当前这一单**重新取值。
   *   不重置的话,关掉再打开看到的是上次改到一半的数 ——
   *   而人会以为那就是这一单现在的值,然后照着它保存。
   */
  function start() {
    setCod(Number(order.codAmount) > 0 || order.codPending === true);
    setPending(order.codPending === true);
    setAmount(Number(order.codAmount) > 0 ? Money.toMajorString(Money.minor(Number(order.codAmount))) : '');
    setItems((order.specialItems ?? []).map((i) => i.code));
    setReason('');
    setErr(null);
    setOpen(true);
  }

  async function save() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/orders/${order.id}/handling`, {
        method: 'PATCH',
        body: {
          codAmount: cod && !pending ? (Money.tryFromMajor(amount) ?? 0) : 0,
          codPending: cod && pending,
          ...(specialItemsOn ? { specialItems: items } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      });
      setOpen(false);
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '改不了');
    } finally {
      setBusy(false);
    }
  }

  if (!canEdit || done) return null;

  if (!open) {
    return (
      <button
        onClick={start}
        className="text-sm text-brand-600 hover:underline dark:text-brand-500"
      >
        改代收 / 特殊物品
      </button>
    );
  }

  const codInvalid = cod && !(Number(amount) > 0);

  return (
    <div
      aria-label="改代收和特殊物品"
      className="rounded-xl border border-brand-300 bg-brand-50/40 p-4 dark:border-brand-700
                 dark:bg-brand-500/10"
    >
      {err && (
        <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>
      )}

      <label className="flex items-center gap-2 py-1">
        <input
          type="checkbox"
          aria-label="货到付款"
          checked={cod}
          onChange={(e) => {
            setCod(e.target.checked);
            if (!e.target.checked) setAmount('');
          }}
        />
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">货到付款</span>
      </label>

      {cod && (
        <div className="mb-3 pl-6">
          <label className="mb-1 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              aria-label="金额待定"
              checked={pending}
              onChange={(e) => setPending(e.target.checked)}
            />
            <span className="text-slate-600 dark:text-slate-300">金额待定(还没算出来)</span>
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-slate-600 dark:text-slate-400">要收多少(元)</span>
            <input
              inputMode="decimal"
              aria-label="代收金额(元)"
              value={amount}
              disabled={pending}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={pending ? '等金额出来再填' : '120'}
              className="h-9 w-40 rounded-lg border border-slate-300 bg-white px-3
                         text-right font-mono disabled:opacity-50
                         dark:border-slate-600 dark:bg-slate-800"
            />
          </label>
          <div className="mt-1 text-xs text-slate-400">
            {/* ★ 待定的单库里存的是 0 —— 照着念「现在是 $0.00」会让人以为不用收 */}
            现在是 {order.codPending ? '待定,司机端显示「联系商家确认」' : money(order.codAmount, order.currency)}
          </div>
        </div>
      )}

      {specialItemsOn && (
        <div className="mb-3">
          <div className="mb-1 text-sm text-slate-600 dark:text-slate-400">特殊物品</div>
          <div className="flex flex-wrap gap-2">
            {dict.map((d) => {
              const on = items.includes(d.code);
              return (
                <button
                  key={d.code}
                  type="button"
                  aria-label={`特殊物品 ${d.label}`}
                  aria-pressed={on}
                  onClick={() =>
                    setItems((cur) =>
                      cur.includes(d.code) ? cur.filter((c) => c !== d.code) : [...cur, d.code],
                    )
                  }
                  className={`rounded-full border px-3 py-1.5 text-sm ${
                    on
                      ? 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300'
                      : 'border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300'
                  }`}
                >
                  {d.icon ? `${d.icon} ` : ''}
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <label className="mb-3 block text-sm">
        <span className="mb-1 block text-slate-600 dark:text-slate-400">为什么改(选填)</span>
        <input
          aria-label="改动原因"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="例:客户少订了一件"
          className="h-9 w-full max-w-sm rounded-lg border border-slate-300 bg-white px-3
                     dark:border-slate-600 dark:bg-slate-800"
        />
      </label>

      {/*
        ★ 货已经在司机手上时要提醒一句。
          司机端是离线优先的,他手机里那份还是旧数 ——
          拿着旧数去收钱的话,送达那一步会被拦下(金额与订单不符)。
          不说的话,调度会以为改完就万事大吉。
      */}
      {order.pickedUpAt && (
        <p className="mb-3 text-xs text-amber-700 dark:text-amber-400">
          ⚠ 货已经在司机手上 —— 他要等下一次同步才看到新的数,最好同时打个电话
        </p>
      )}

      <div className="flex items-center gap-2">
        <button
          onClick={() => void save()}
          disabled={busy || codInvalid}
          className="h-9 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white
                     disabled:opacity-40"
        >
          {busy ? '保存中…' : '保存'}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="h-9 rounded-lg border border-slate-300 px-3 text-sm text-slate-700
                     dark:border-slate-600 dark:text-slate-300"
        >
          取消
        </button>
        {codInvalid && <span className="text-xs text-red-600">勾了货到付款就得填金额</span>}
      </div>
    </div>
  );
}
